import router from '@/router'
import { UserService } from '@/services/user.service'
import { SupplierService } from '@/services/supplier.service'
import { SearchService } from '@/services/search.service'
import { MenuService } from '@/services/menu.service'
import { ProfileService } from '@/services/profile.service'
import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import {
  mapUserWithStatus,
  mapUserWithDealer,
  splitGroupsByParent,
  mapDealerOption,
  isOk,
  commitPaginationFromMeta,
  showErrorToast
} from './helpers'

export const userActions = {
  async loadUserById({ commit }, id) {
    const response = await UserService.user(id)
    const userData = response.data.data
    commit('SET_USER_MODEL', userData)
    commit('SET_INITIAL_USER_ROLE', Array.isArray(userData.modelRoles) ? userData.modelRoles : [])

    if (userData.map) {
      commit('SET_PARENT_ATTRIBUTES', {
        map: userData.map,
        nonCommSel: userData.nonCommSel
      })
    } else {
      commit('SET_PARENT_ATTRIBUTES', null)
    }
    commit('SET_PARENT_ATTRIBUTE_PENDING', false)
  },

  async loadUsers({ commit }, data) {
    let response = null

    if (data?.id) {
      response = await UserService.rolesUsers(data.id)
    } else {
      response = await UserService.users(data.page, data.status, data.show_points)
    }

    let users = []
    if (response.data.data) {
      users = response.data.data.map((user) => mapUserWithStatus(user, { withIbanText: true, includeCompany: true }))
      commit('SET_USERS', users)
      commitPaginationFromMeta(commit, response)
    }
  },

  async loadUsersList({ commit }, data) {
    const response = await UserService.usersList(data.page)
    let users = []
    if (response.data.data) {
      users = response.data.data.map((user) => mapUserWithStatus(user, { withIbanText: true, includeCompany: true }))
      commit('SET_USERS', users)
      commitPaginationFromMeta(commit, response)
    }
  },

  async loadSearchFilter({ commit, state }, data) {
    try {
      if (state.usersPending) {
        return true
      } else {
        commit('SET_USERS_PENDING', true)
        const response = await UserService.filterSearch(data)
        let users = []
        if (response.data.data) {
          users = response.data.data.map((user) => ({
            ...mapUserWithStatus(user, { withIbanText: true, includeCompany: true }),
            group: user.group
          }))
          commit('SET_USERS', users)
          commitPaginationFromMeta(commit, response)
          commit('SET_USERS_PENDING', false)
        }
      }
    } catch (err) {
      showErrorToast(err)
      commit('SET_USERS_PENDING', false)
      return err
    }
  },

  async uploadImage({ commit }, data) {
    try {
      commit('SET_IS_LOADING', true)
      const response = await UserService.uploadImage(data.id, data.form)

      if (response) {
        commit('SET_IS_LOADING', false)
        router.push({ name: 'product', params: { id: data.id } })
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadLanguages({ commit }) {
    try {
      const response = await UserService.languages()
      commit('SET_LANGS', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadVendorsForSelect({ commit }) {
    try {
      const response = await UserService.vendorsSelect()
      commit('SET_VENDORS_SELECT', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadSuppliersForSelect({ commit }) {
    try {
      const response = await SupplierService.suppliersSelect()
      commit('SET_SUPPLIERS_SELECT', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadAttributesForSelect({ commit }) {
    try {
      const response = await UserService.attributesSelect()
      commit('SET_ATTRIBUTES_SELECT', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadAttributes({ commit }) {
    try {
      const response = await UserService.attributes()
      commit('SET_ATTRIBUTES', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadOptions({ commit }) {
    try {
      const response = await UserService.options()
      commit('SET_OPTIONS', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadOptionsSelect({ commit }) {
    try {
      const response = await UserService.optionsSelect()
      commit('SET_OPTIONS_SELECT', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadFilter({ commit }) {
    try {
      const response = await UserService.filter()
      commit('SET_FILTER', response.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async searchCustomer({ commit }, search) {
    try {
      const response = await UserService.search(search)

      if (response.status === HTTP_STATUS_CODES.OK && response.data.data) {
        commit('SET_CUSTOMER', response.data.data)
      } else {
        commit('SET_CUSTOMER', {})
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadLoginHistory({ commit }, data) {
    const response = await UserService.loadUserLoginHistory(data.id, data.page)
    commit('SET_LOGIN_HISTORY', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadLoginsHistory({ commit }, data) {
    const response = await UserService.loadLoginHistory(data)
    commit('SET_LOGIN_HISTORY', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadInactiveUsers({ commit }, data) {
    try {
      const response = await UserService.fetchInactiveUsers(data)
      let dealers = []
      if (response.data.data) {
        dealers = response.data.data.map((user) =>
          mapUserWithDealer(user, {
            withIbanText: true,
            withCustomerData: true,
            includeCompany: true
          })
        )
        commit('SET_CS_NUMBERS_TO_APPROVE', dealers)
        commit('SET_INACTIVE_USERS', dealers)
        commitPaginationFromMeta(commit, response)
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadPendingIndirectUsers({ commit }, data) {
    try {
      const response = await UserService.fetchPendingIndirect(data)
      let dealers = []
      if (response.data.data) {
        dealers = response.data.data.map((user) =>
          mapUserWithDealer(user, {
            withIbanText: false,
            withCustomerData: true,
            includeCompany: true,
            includeValidationKey: true
          })
        )
        commit('SET_CS_NUMBERS_TO_APPROVE', dealers)
        commit('SET_INACTIVE_USERS', dealers)
        commitPaginationFromMeta(commit, response)
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadCsNumbersToApprove({ commit }, data) {
    try {
      const response = await UserService.fetchCustomerNumbersToApprove(data)
      let dealers = []
      if (response.data.data) {
        dealers = response.data.data.map((user) => mapUserWithDealer(user))
        commit('SET_CS_NUMBERS_TO_APPROVE', dealers)
        commitPaginationFromMeta(commit, response)
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async doMatchByUserID({ commit }, id) {
    const response = await ProfileService.doMatch(id)
    let text_validate = null
    console.log('response = ')
    if (response.data.iban_status) {
      text_validate = ProfileService.getMessageByStatusIban(response.data.iban_status, 1)
    }
    const status_match = {
      statusMatch: response.data.iban_status.statusMatch,
      message: text_validate,
      showMessageIbanStatus: true
    }
    commit('SET_MESSAGE_STATUS_MATCH', status_match)
  },

  setIbanStatus({ commit }, data) {
    const status_match = {
      statusMatch: data.statusMatch,
      message: data.message,
      showMessageIbanStatus: data.showMessageIbanStatus
    }
    commit('SET_MESSAGE_STATUS_MATCH', status_match)
  },

  async loadDealers({ commit }) {
    const response = await UserService.loadDealersUsers()
    let dealers = []

    if (response) {
      dealers = response.map((user) => mapDealerOption(user, { includeEmail: true }))
      commit('SET_DEALERS', dealers)
    }
  },

  async loadWholesalers({ commit }) {
    try {
      const response = await UserService.loadWholesalersUsers()
      commit('SET_WHOLESALERS', response.allWholesalers)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadUserOrders({ commit }, data) {
    try {
      const response = await UserService.loadUserOrders(data.id, data.page)
      commit('SET_USER_ORDERS', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadBankStatement({ commit }, data) {
    try {
      const response = await UserService.loadBankStatement(data.id, data.page)
      commit('SET_BANK_STATEMENT', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadAllBankStatement({ commit }, data) {
    try {
      const response = await UserService.loadAllBankStatement(data.page)
      commit('SET_BANK_STATEMENT', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadDeletedUsers({ commit }, data) {
    try {
      const page = typeof data === 'object' && data !== null ? data.page : data
      const search = typeof data === 'object' && data !== null ? data.search : ''
      const response = await UserService.loadDeletedUsers(page, search)
      commit('SET_DELETED_USERS', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadStaffs({ commit }) {
    try {
      const response = await UserService.staffs()
      commit('SET_STAFFS', response.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadActivities({ commit }, { page = 1, userHashId, search }) {
    try {
      const response = await UserService.getActivities(userHashId, page, search)
      if (isOk(response)) {
        commit('SET_ACTIVITIES', response.data.data)
        commit('SET_PAGINATION_ACTIVITY', response.data.meta)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadGlobalActivities({ commit }, { page = 1, search }) {
    try {
      const response = await UserService.getGlobalActivities(page, search)
      if (isOk(response)) {
        commit('SET_ACTIVITIES_GLOBAL', response.data.data)
        commit('SET_PAGINATION_ACTIVITY_GLOBAL', response.data.meta)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async filterActivities({ commit }, { page = 1, types, authors, modified_users }) {
    try {
      const response = await UserService.filterActivity(page, types, authors, modified_users)
      if (isOk(response)) {
        commit('SET_ACTIVITIES_GLOBAL', response.data.data)
        commit('SET_PAGINATION_ACTIVITY_GLOBAL', response.data.meta)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadActivityFilters({ commit }) {
    try {
      const response = await UserService.getActivityFilters()
      if (isOk(response)) {
        commit('SET_ACTIVITY_FILTERS', response.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async searchUsers({ commit }, data) {
    try {
      const response = await SearchService.search(data)
      commit('SET_USERS', response.data.data)
      commit('SET_PAGINATION', null)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async filterUsers({ commit }, data) {
    try {
      const response = await SearchService.filter(data)
      commit('SET_USER_FILTERS', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async filterMenu({ commit }, data) {
    try {
      const response = await MenuService.filter(data)
      commit('SET_MENU_FILTERS', response.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  setUserFilters({ commit }, data) {
    commit('SET_FILTERS_USER', data)
  },

  async loadUserApps({ commit }, payload) {
    const id = typeof payload === 'object' ? payload.id : payload
    const plainId = typeof payload === 'object' ? payload.plainId : null
    let targetPlainId = plainId

    if (!targetPlainId) {
      const userResponse = await UserService.user(id)
      targetPlainId = userResponse.data.data.plainId
    }

    const response = await UserService.userApps(targetPlainId)
    commit('SET_USER_APPS', response.data)
  },

  addInitialParentAttributes({ commit }, data) {
    commit('SET_INITIAL_PARENT_ATTRIBUTES', data)
  },

  async loadThreads({ commit }) {
    try {
      const response = await UserService.getThreads()
      commit('SET_THREADS', response.data)
    } catch (err) {
      showErrorToast(err)
      console.error(err)
    }
  },

  async loadThreadsBySlug({ commit }, slug) {
    try {
      const response = await UserService.loadThreadsBySlug(slug)
      if (isOk(response)) {
        commit('SET_THREADS_BY_SLUG', {
          data: response.data,
          slug: slug
        })
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadEditUserInfo({ commit }) {
    const response = await UserService.fetchEditUserInfo()

    commit('SET_LANGS', response.data.languages)
    commit('SET_WHOLESALERS', response.data.allWholesalers)
    commit('SET_ROLES_SELECT', response.data.roles)
    commit('SET_ROLES', response.data.roles)
    commit('SET_STAFFS', response.data.staffs)

    const groups = response.data.groups
    const { parents, children } = splitGroupsByParent(groups)
    commit('SET_GROUPS', children)
    commit('SET_PARENT_GROUPS', parents)
    commit('SET_CUSTOMER_GROUPS', response.data.customerGroups)
    commit('SET_CAMPAIGNS', response.data.campaigns)
    commit('SET_CAMPAIGN_TYPES', response.data.campaignTypes || [])
  },

  async saveUser({ commit, state }, data) {
    try {
      const response = await UserService.saveUser(data, data.id)
      if (response) {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadAttributeById({ commit }, id) {
    const response = await UserService.attribute(id)
    commit('SET_ATTRIBUTE', response.data.data)
  },

  async loadOptionById({ commit, dispatch }, id) {
    const response = await UserService.option(id)
    commit('SET_OPTION', response.data.data)

    if (response) {
      dispatch('loadLanguages')
    }
  },

  async storeOption({ commit }, data) {
    try {
      const response = await UserService.storeOption(data)

      if (response) {
        router.push({ name: 'options_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async saveOption({ commit }, data) {
    try {
      const response = await UserService.saveOption(data, data.id)

      if (response) {
        router.push({ name: 'options_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async saveAttribute({ commit, state }, data) {
    try {
      const response = await UserService.saveAttribute({ ...data, ...{ options: state.optionsModel } }, data.id)

      if (response) {
        router.push({ name: 'attributes_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async storeAttribute({ commit, state }, data) {
    try {
      const response = await UserService.storeAttribute({
        ...data,
        ...{ options: state.optionsModel }
      })

      if (response) {
        router.push({ name: 'attributes_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  }
}
